import React, {FC, memo} from "react";
import {Alert, Table, Tag, Tooltip} from "antd";
import {CheckCircleOutlined, EuroCircleOutlined, LockOutlined} from "@ant-design/icons";
import {ColumnProps, TablePaginationConfig} from "antd/lib/table";
import {ApolloError} from "@apollo/client";
import moment, {Moment} from "moment";
import classNames from "classnames";
import {green, red, yellow} from "@ant-design/colors";
import {
    ArrayDataOptions,
    GetBookingsByCourseIdWithCourseLessonBookingsQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    LessonBookingPlannerPreviewQuery,
    LessonBookingPlannerRateStatus,
    LessonBookingPlannerUnitStatus,
    Maybe,
    PaymentType,
} from "../../../generated/graphql";
import SummarySection from "../../../views/SummarySection/SummarySection";
import LoadingContent from "../../../views/LoadingContent/LoadingContent";
import {renderAmount} from "../../../helpers/renderAmount";
import {getPagination} from "../../../helpers/getPagination";
import {renderDateAsWeekdayTag, renderDateFormatted} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {colorGrey} from "../../../styles/colors";
import i18n from "../../../services/i18n";
import Notes from "./Notes";
import {DummySwitchMonthlyPayment} from "./PaymentLists/SwitchMonthlyPayment";
import paymentCss from "./PaymentLists/PaymentLists.less";
import {renderIndex} from "./helper";
import {defaultPageSize} from "./ModalContainer";
import css from "./LessonBookings.less";

// Vorschau „Neu“ (Superuser), wie die bisherige Ansicht: links „Bisherige Buchung“, rechts „Vorschau der geänderten Buchung“.
// Beide Spalten entstehen aus EINER Zeilenliste (Monate, Kurseinheiten, Raten) mit fester Zeilenhöhe, damit Einheit n links
// und rechts auf gleicher Höhe steht; was es nur auf einer Seite gibt, bekommt gegenüber eine leere Zeile. Raten und
// „Gesamtpreis“ sehen aus wie in der bisherigen Ansicht (PaymentLists). Geändertes wird rechts hervorgehoben, darunter:
// was sich ändert, was daraus folgt, was das Büro entscheidet. Nur lesend.

type PlannerData = LessonBookingPlannerPreviewQuery["lessonBookingPlannerPreview"];
type PlannerRate = PlannerData["rates"][number];

type MonthRow = {key: string; label: string; before: number; after: number; removed: Array<string>; added: Array<string>; moved: Array<string>};

type LessonRow = {
    key: string;
    index: number;
    startDateTime: Date | string;
    before: boolean;
    after: boolean;
    status?: LessonBookingPlannerUnitStatus;
    movedFrom?: string;
    unresolved?: boolean;
    existed: boolean; // Einheit gab es schon vor der Änderung (sonst links leere Zeile)
};
type FormLesson = {id?: string | null; startDateTime: Date | string; indexOfLessonInCourse?: number | null};

type PlannerPreviewProps = {
    data?: PlannerData;
    loading: boolean;
    error?: ApolloError;
    booking?: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
    lessonsOfCourse?: Array<FormLesson>; // Kurseinheiten aus dem Formular (nach der Änderung); ohne id = neu, Platzhalter wie im Planner
    getHolidayInfo?: (date: Moment) => GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined;
    options?: Maybe<ArrayDataOptions>; // Blättern, geteilt mit der bisherigen Ansicht
    setOptions?: (options: ArrayDataOptions) => void;
    allowMonthlyPayment?: boolean; // für die Zahlweise-Anzeige wie in der bisherigen Ansicht
    existingLessonIds?: Set<string>; // IDs der gespeicherten Kurseinheiten; „Einheit hinzufügen“ vergibt schon eine vorläufige ID
};

const eur = (amount: number) => renderAmount({amount});
const date = (value: string | Date) => moment(value).format("DD.MM.YYYY");
const day = (value: string | Date) => moment(value).format("DD.MM.");
const changed = (a: number, b: number) => Math.abs(a - b) > 0.005;
const monthChanged = (row: MonthRow) => row.removed.length + row.added.length + row.moved.length > 0;
const changedCell = {backgroundColor: yellow[1], fontWeight: 600};
const blank = <span style={{color: "#bbb"}}>{"–"}</span>;

const signed = (amount: number) => (amount < 0 ? `− ${eur(Math.abs(amount))}` : `+ ${eur(amount)}`);
const deltaText = (delta: number, unit: "units" | "eur") => {
    if (unit === "units" ? delta === 0 : !changed(delta, 0)) return null;
    const text = unit === "units" ? `${delta > 0 ? "+" : "−"}${Math.abs(delta)}` : signed(delta);

    return <span style={{color: delta < 0 ? red[6] : green[7], fontWeight: 600}}>{text}</span>;
};

// Heute zählt am alten Datum, Neu am neuen (verschobene Einheit: gleiche ID, anderer Tag → beide Monate mit Hinweis).
const groupByMonth = (units: PlannerData["units"]): Array<MonthRow> => {
    const rows = new Map<string, MonthRow>();
    const rowFor = (value: string | Date) => {
        const m = moment(value);
        const key = m.format("YYYY-MM");
        const row = rows.get(key) ?? {key, label: m.format("MMMM YYYY"), before: 0, after: 0, removed: [], added: [], moved: []};

        rows.set(key, row);

        return row;
    };

    units.forEach((unit) => {
        const before = rowFor(unit.lessonDate);
        const after = rowFor(unit.movedTo ?? unit.lessonDate);

        if (unit.status !== LessonBookingPlannerUnitStatus.Added) before.before += 1;
        if (unit.status !== LessonBookingPlannerUnitStatus.Removed) after.after += 1;
        if (unit.status === LessonBookingPlannerUnitStatus.Removed) before.removed.push(day(unit.lessonDate));
        if (unit.status === LessonBookingPlannerUnitStatus.Added) after.added.push(day(unit.lessonDate));
        if (unit.movedTo) {
            const text = `${day(unit.lessonDate)} → ${day(unit.movedTo)}`;

            before.moved.push(text);
            if (after !== before) after.moved.push(text);
        }
    });

    return Array.from(rows.values()).sort((a, b) => a.key.localeCompare(b.key));
};

// Alle Kurseinheiten mit Haken „Heute“ und „Neu“; aus dem Kurs gelöschte Einheiten kommen aus den Planner-Units dazu.
// Ohne `existingLessonIds` zählt eine Einheit mit ID als vorhanden (nur „Neu generieren“ erzeugt Einheiten ohne ID).
export const lessonRows = (units: PlannerData["units"], lessonsOfCourse: Array<FormLesson>, existingLessonIds?: Set<string>): Array<LessonRow> => {
    const unitsById = new Map(units.map((unit) => [unit.lessonId, unit]));
    const rows: Array<LessonRow> = lessonsOfCourse.map((lesson, index) => {
        const id = lesson.id ?? `new-${index}`;
        const unit = unitsById.get(id);

        unitsById.delete(id);

        return {
            key: id,
            index,
            startDateTime: lesson.startDateTime,
            before: !!unit && unit.status !== LessonBookingPlannerUnitStatus.Added,
            after: !!unit && unit.status !== LessonBookingPlannerUnitStatus.Removed,
            status: unit?.status,
            // Die Zeile trägt das neue Datum aus dem Formular; links steht das alte, auch wenn die Einheit zugleich wegfällt
            // (der Planner setzt movedTo nur für behaltene Einheiten).
            movedFrom: unit && !moment(unit.lessonDate).isSame(lesson.startDateTime, "day") ? unit.lessonDate : undefined,
            existed: Boolean(lesson.id) && (existingLessonIds ? existingLessonIds.has(lesson.id as string) : true),
        };
    });

    // Einheiten ohne Treffer im Formular: aus dem Kurs gelöscht (Removed) oder nach „Neu generieren“ mit alter ID als Kept (Zuordnung offen).
    unitsById.forEach((unit) =>
        rows.push({
            key: unit.lessonId,
            index: -1,
            startDateTime: unit.lessonDate,
            before: unit.status !== LessonBookingPlannerUnitStatus.Added,
            after: unit.status !== LessonBookingPlannerUnitStatus.Removed,
            status: unit.status,
            unresolved: unit.status === LessonBookingPlannerUnitStatus.Kept,
            existed: true,
        }),
    );
    rows.sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());
    // Nummer = Position in der sortierten Gesamtliste, links wie rechts dieselbe. Die Kursnummerierung rückt nach einer
    // Löschung nach (13.03. würde zur 5), der Vergleich nicht: gelöschte 06.03. bleibt die 5, 13.03. die 6.
    rows.forEach((row, position) => {
        row.index = position;
    });

    return rows;
};

const PlannerPreview: FC<PlannerPreviewProps> = ({data, loading, error, booking, lessonsOfCourse, getHolidayInfo, options, setOptions, allowMonthlyPayment, existingLessonIds}) => {
    const panels = (left: React.ReactNode, right: React.ReactNode) => (
        // height: auto statt 100 % aus dem Stylesheet: sonst laufen die Spalten über und die Hinweise darunter überlagern sie.
        <div className={css.bookingContainer} style={{height: "auto"}}>
            <div className={css.modalContainerLeft}>
                <div className={css.header}>{"Bisherige Buchung"}</div>
                {left}
            </div>
            <div className={css.modalContainerRight}>
                <div className={css.header}>{"Vorschau der geänderten Buchung"}</div>
                {right}
            </div>
        </div>
    );

    if (error) {
        return panels(
            null,
            <div style={{padding: "1rem"}}>
                <Alert type="error" showIcon message="Vorschau (neues Modell) konnte nicht berechnet werden" description={error.message} />
            </div>,
        );
    }
    if (loading || !data) {
        return panels(
            null,
            <div style={{height: "70%"}}>
                <LoadingContent content="Vorschau (neues Modell) wird berechnet..." />
            </div>,
        );
    }

    const months = groupByMonth(data.units);
    const priceChanged = changed(data.priceAfter, data.priceBefore);
    const boundCount = data.rates.filter((r) => r.status === LessonBookingPlannerRateStatus.Bound).length;
    const hasUnitPrice = data.unitPrice !== null && data.unitPrice !== undefined;

    // Auch Ersatz im selben Monat (Ausfall + Zubuchung, Saldo 0) ist eine Änderung.
    const changes: Array<string> = data.regenerated
        ? ["Alle Einheiten wurden neu erzeugt: Zuordnung zur Buchung offen, Bestand bleibt stehen"]
        : months.filter(monthChanged).map((m) => {
              const delta = m.after - m.before;

              return `${m.label}: ${delta > 0 ? "+" : delta < 0 ? "−" : "±"}${Math.abs(delta)} Einheiten (${[...m.removed, ...m.added, ...m.moved].join(", ")})`;
          });

    changes.push(priceChanged ? `Preis ${eur(data.priceBefore)} → ${eur(data.priceAfter)}` : "Preis unverändert");
    if (changes.length === 1) changes.unshift("Keine Änderung an den Einheiten");

    const consequences: Array<string> = data.rates
        .filter((r) => r.status === LessonBookingPlannerRateStatus.New || changed(r.amountAfter, r.amountBefore))
        .map((r) =>
            r.status === LessonBookingPlannerRateStatus.New ? `Neue Rate ${date(r.paymentDue)}: ${eur(r.amountAfter)}` : `Rate ${date(r.paymentDue)}: ${eur(r.amountBefore)} → ${eur(r.amountAfter)}`,
        );

    if (data.credit > 0.005) consequences.push(`Guthaben ${eur(data.credit)}: kein offener Posten kann den Ausgleich tragen`);
    if (consequences.length === 0) consequences.push("Raten unverändert");
    if (boundCount > 0) consequences.push(`${boundCount} gebundene ${boundCount === 1 ? "Rate bleibt" : "Raten bleiben"}, wie sie ${boundCount === 1 ? "ist" : "sind"}`);

    // Gesamtpreis = Einheiten × Einheitenpreis + Altbestand + Gebühren/Zusatzposten/Gutschriften (Planner-Invariante, beide Seiten).
    // Die Gleichung steht nur, wenn sie aufgeht; sonst erklären die Hinweise den Preis.
    const term = (amount: number, label: string) => (changed(amount, 0) ? ` ${amount < 0 ? "−" : "+"} ${eur(Math.abs(amount))} ${label}` : "");
    const priceFormula = (after: boolean) => {
        if (!hasUnitPrice) return "Pauschalpreis pro Kurs";
        if (after && data.canceled) return "unverändert";
        const units = after ? data.bookedUnitsAfter : data.bookedUnitsBefore;
        const price = after ? data.priceAfter : data.priceBefore;

        // Bei storniert oder „neu erzeugt“ rechnet der Planner keinen Altbestand aus: dann lieber nichts als ein Hinweis, den es nicht gibt.
        if (changed(units * (data.unitPrice as number) + data.legacyGap + data.nonCoursePostsSum, price)) return data.canceled || data.regenerated ? "" : "siehe Hinweise";

        return `${units} × ${eur(data.unitPrice as number)}${term(data.legacyGap, "„Altbestand“")}${term(data.nonCoursePostsSum, "Gebühren/Zusatzposten/Gutschriften")}`;
    };

    const lessons = lessonsOfCourse ? lessonRows(data.units, lessonsOfCourse, existingLessonIds) : [];
    // Beide Terminlisten blättern über dieselben Optionen, sonst zeigen sie verschiedene Seiten.
    const pagination: TablePaginationConfig = {
        current: options?.offset && options?.limit ? options.offset / options.limit + 1 : 1,
        pageSize: options?.limit ?? defaultPageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "15", "20", "25", "50", "100"],
        total: lessons.length,
        size: "small",
        showTotal: (total, range) => `${range[0]}-${range[1]} von ${total}`,
    };
    const onTableChange = (next: TablePaginationConfig) => {
        const {limit, offset} = getPagination({pagination: next, defaultSize: defaultPageSize});

        setOptions?.({...options, limit, offset});
    };

    // Spalten je Seite: links der Stand heute ohne Hervorhebung, rechts der neue Stand mit Delta, Grund und gelben Zellen.
    const monthColumns = (after: boolean): Array<ColumnProps<MonthRow>> => [
        {title: "Monat", dataIndex: "label", key: "label"},
        ...(after ? [{title: "", key: "delta", align: "center" as const, width: 50, render: (_: unknown, row: MonthRow) => deltaText(row.after - row.before, "units")}] : []),
        {
            title: "Einheiten",
            key: "units",
            align: "right",
            width: 90,
            onCell: (row) => ({style: after && monthChanged(row) ? changedCell : {}}),
            render: (_, row) => (after ? row.after : row.before),
        },
        ...(after
            ? [
                  {
                      title: "Grund",
                      key: "reason",
                      ellipsis: true,
                      onCell: (row: MonthRow) => ({style: monthChanged(row) ? changedCell : {}}),
                      render: (_: unknown, row: MonthRow) => (
                          <>
                              {row.removed.length > 0 && <span style={{color: red[6]}}>{`Ausfall ${row.removed.join(", ")}`}</span>}
                              {row.added.length > 0 && <span style={{color: green[7]}}>{`${row.removed.length > 0 ? " · " : ""}Zubuchung ${row.added.join(", ")}`}</span>}
                              {row.moved.length > 0 && <span style={{color: "#666"}}>{`${row.removed.length + row.added.length > 0 ? " · " : ""}Verschoben ${row.moved.join(", ")}`}</span>}
                          </>
                      ),
                  },
              ]
            : []),
    ];

    const lessonColumns = (after: boolean): Array<ColumnProps<LessonRow>> => {
        const shown = (row: LessonRow) => after || row.existed;
        const dateOf = (row: LessonRow) => (after ? row.startDateTime : row.movedFrom ?? row.startDateTime);

        return [
            {title: "#", key: "index", align: "center", width: 50, render: (_, row) => (shown(row) ? renderIndex(row.index, getHolidayInfo?.(moment(dateOf(row)))) : null)},
            {
                title: i18n.containers.courses.CourseCreate.courseStartDateTime.weekday(),
                key: "weekday",
                width: 110,
                render: (_, row) => (shown(row) ? renderDateAsWeekdayTag({value: moment(dateOf(row)).toISOString(), style: {width: "100%", textAlign: "center", fontWeight: 600}}) : null),
            },
            {
                title: i18n.containers.courses.CourseCreate.courseStartDateTime.date(),
                key: "date",
                onCell: (row) => ({style: after && row.movedFrom ? changedCell : {}}),
                render: (_, row) => (shown(row) ? renderDateFormatted(moment(dateOf(row)).toISOString(), "DD. MMM YYYY") : blank),
            },
            ...(after
                ? [
                      {
                          title: "Grund",
                          key: "reason",
                          ellipsis: true,
                          onCell: (row: LessonRow) => ({style: row.before !== row.after || row.movedFrom || row.unresolved ? changedCell : {}}),
                          render: (_: unknown, row: LessonRow) =>
                              row.unresolved ? (
                                  <span style={{color: "#666"}}>{"Zuordnung offen"}</span>
                              ) : row.status === LessonBookingPlannerUnitStatus.Removed ? (
                                  <span style={{color: red[6]}}>{"Ausfall"}</span>
                              ) : row.status === LessonBookingPlannerUnitStatus.Added ? (
                                  <span style={{color: green[7]}}>{"Zubuchung"}</span>
                              ) : row.movedFrom ? (
                                  <span style={{color: "#666"}}>{`Verschoben von ${day(row.movedFrom)}`}</span>
                              ) : null,
                      },
                  ]
                : []),
        ];
    };

    const lessonRowClass = (row: LessonRow, after: boolean) => {
        if (after && row.status === LessonBookingPlannerUnitStatus.Removed) return classNames(css.alignedRow, css.canceledBooking, css.unbookedLesson);
        if (after && row.status === LessonBookingPlannerUnitStatus.Added) return classNames(css.alignedRow, css.newBooking, css.bookedLesson);
        if (!after && !row.existed) return css.alignedRow;

        return classNames(css.alignedRow, (after ? row.after : row.before) ? css.bookedLesson : css.unbookedLesson);
    };

    // Raten wie in der bisherigen Ansicht (PaymentsPassive): Nummer, Monat, „Fällig am“ bzw. Bindungsgrund, Betrag.
    // Feste Mindesthöhe, damit die leere Gegenzeile einer neuen Rate links gleich hoch ist.
    const entryStyle = {minHeight: 26};
    const amountStyle = {whiteSpace: "nowrap" as const}; // Delta + Betrag bleiben eine Zeile, sonst wird die Zeile rechts höher
    const rateEntry = (rate: PlannerRate, position: number, after: boolean) => {
        const key = rate.id ?? `new-${String(rate.paymentDue)}`;
        const isNew = rate.status === LessonBookingPlannerRateStatus.New;

        if (!after && isNew) {
            return (
                <div key={key} className={paymentCss.monthlyPaymentEntry}>
                    <div className={paymentCss.mainDue} style={entryStyle}>
                        <div className={paymentCss.month}>
                            <span className={paymentCss.num}>{"–"}</span>
                        </div>
                    </div>
                </div>
            );
        }
        const bound = rate.status === LessonBookingPlannerRateStatus.Bound;
        const paid = rate.boundReason === "bezahlt"; // „teilbezahlt“ bleibt offen dargestellt (Schloss, kein Grün)
        const delta = after ? rate.amountAfter - rate.amountBefore : 0;
        const isChanged = after && !isNew && changed(delta, 0); // neue Rate: „neu“ statt Delta
        const adjustments = rate.adjustments.map((adjustment) => `${adjustment.reason} ${signed(adjustment.amount)}`).join(", ");

        return (
            <div key={key} className={classNames(paymentCss.monthlyPaymentEntry, isChanged && paymentCss.changedPayment)} style={after && isNew ? {backgroundColor: green[1]} : undefined}>
                <div className={paid ? paymentCss.mainDone : paymentCss.mainDue} style={entryStyle}>
                    <div className={paymentCss.month}>
                        <span className={paymentCss.num}>{`${position + 1}.`}</span>
                        <span>{moment(rate.paymentDue).format("MMM YY")}</span>
                        {after && isNew && (
                            <Tag color="green" style={{marginLeft: 8}}>
                                {"neu"}
                            </Tag>
                        )}
                    </div>
                    <div className={paymentCss.paymentDueContainer}>
                        <div className={paymentCss.paymentDue}>{bound ? rate.boundReason : `Fällig am ${date(rate.paymentDue)}`}</div>
                        {paid ? (
                            <CheckCircleOutlined style={{color: green[5], fontSize: 13}} />
                        ) : bound ? (
                            <LockOutlined style={{color: colorGrey, fontSize: 13}} />
                        ) : (
                            <EuroCircleOutlined style={{color: colorGrey, fontSize: 13}} />
                        )}
                    </div>
                    <div className={paymentCss.amount} style={amountStyle}>
                        {isChanged && (
                            <Tooltip title={adjustments || undefined}>
                                <span style={{marginRight: 8}}>{deltaText(delta, "eur")}</span>
                            </Tooltip>
                        )}
                        {eur(after ? rate.amountAfter : rate.amountBefore)}
                    </div>
                </div>
            </div>
        );
    };

    // „Gesamtpreis“ wie in der bisherigen Ansicht; die gelbe Markierung tragen beide Seiten, damit sie gleich hoch bleiben.
    const totalPrice = (after: boolean) => (
        <>
            <div className={classNames(paymentCss.totalPrice, priceChanged && paymentCss.totalPriceChanged)}>
                <div>{"Gesamtpreis"}</div>
                <div style={amountStyle}>
                    {after && priceChanged && <span style={{fontSize: 14, marginRight: 8}}>{deltaText(data.priceAfter - data.priceBefore, "eur")}</span>}
                    {eur(after ? data.priceAfter : data.priceBefore)}
                </div>
            </div>
            <div className={paymentCss.totalPriceSub} style={{minHeight: 18}}>
                {priceFormula(after)}
            </div>
        </>
    );

    const side = (after: boolean) => (
        <div className={css.bookingRoot}>
            <SummarySection
                title="Kurseinheiten je Monat"
                content={<Table<MonthRow> size="small" pagination={false} rowKey="key" dataSource={months} columns={monthColumns(after)} rowClassName={() => css.alignedRow} />}
            />
            {lessonsOfCourse && (
                <SummarySection
                    title={i18n.containers.bookings.BookingSummary.sectionHeaders.courseLessons()}
                    style={{marginTop: 12}}
                    content={
                        <Table<LessonRow>
                            className="lessonSelectionTable"
                            size="small"
                            rowKey="key"
                            dataSource={lessons}
                            columns={lessonColumns(after)}
                            rowSelection={{
                                type: "checkbox",
                                hideSelectAll: true,
                                selectedRowKeys: lessons.filter((row) => (after ? row.after : row.before)).map((row) => row.key),
                                getCheckboxProps: (row) => ({disabled: true, style: after || row.existed ? undefined : {visibility: "hidden"}}),
                            }}
                            rowClassName={(row) => lessonRowClass(row, after)}
                            pagination={pagination}
                            onChange={onTableChange}
                        />
                    }
                />
            )}
            <SummarySection
                title={i18n.containers.bookings.BookingSummary.sectionHeaders.installmentRates()}
                style={{marginTop: 12}}
                content={
                    <div>
                        {booking && <DummySwitchMonthlyPayment paymentType={booking.paymentType as PaymentType} allowUpFrontPayment={true} allowMonthlyPayment={allowMonthlyPayment} />}
                        {data.rates.map((rate, position) => rateEntry(rate, position, after))}
                        {totalPrice(after)}
                    </div>
                }
            />
            {booking && <SummarySection style={{marginTop: 12}} title={i18n.containers.bookings.BookingSummary.sectionHeaders.notes()} content={<Notes booking={booking} />} />}
        </div>
    );

    const list = (items: Array<string>) => (
        <ul style={{margin: 0, paddingLeft: "1.25rem"}}>
            {items.map((item, index) => (
                <li key={index}>{item}</li>
            ))}
        </ul>
    );

    const decision = data.canceled ? (
        <Alert
            type="info"
            showIcon
            message="Buchung ist storniert"
            description={
                <>
                    {"Einheiten werden abgeglichen, Preis und Raten bleiben unverändert."}
                    {data.warnings.length > 0 && list(data.warnings)}
                </>
            }
        />
    ) : data.warnings.length === 0 ? (
        <Alert type="success" showIcon message="Nichts zu entscheiden" description="Buchung konsistent: Preis = Einheiten × Einheitenpreis, Raten decken den Preis." />
    ) : (
        <Alert type="warning" showIcon message="Was das Büro entscheiden muss" description={list(data.warnings)} />
    );

    return (
        <>
            {panels(side(false), side(true))}
            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8}}>
                <Alert type="info" message="Was sich ändert" description={list(changes)} />
                <Alert type="info" message="Was daraus folgt" description={list(consequences)} />
                {decision}
            </div>
        </>
    );
};

export default memo(PlannerPreview);
